import React, { useState } from 'react';
import {
  FileText,
  Upload,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Eye,
  MessageSquare,
  Star,
  ChevronDown,
  ChevronUp,
  Calendar,
  User
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { formatDistanceToNow, format } from 'date-fns';

const TASK_STATUS_CONFIG = {
  PENDING: {
    label: 'Pending',
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    icon: Clock,
    bgColor: 'bg-gray-50'
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: RefreshCw,
    bgColor: 'bg-blue-50'
  },
  SUBMITTED: {
    label: 'Submitted',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: Upload,
    bgColor: 'bg-purple-50'
  },
  UNDER_REVIEW: {
    label: 'Under Review',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Eye,
    bgColor: 'bg-amber-50'
  },
  REVISION_REQUIRED: {
    label: 'Revision Required',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    icon: AlertCircle,
    bgColor: 'bg-orange-50'
  },
  COMPLETED: {
    label: 'Completed',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle,
    bgColor: 'bg-green-50'
  }
};

const PRIORITY_CONFIG = {
  LOW: { label: 'Low', color: 'bg-gray-100 text-gray-600' },
  MEDIUM: { label: 'Medium', color: 'bg-blue-100 text-blue-600' },
  HIGH: { label: 'High', color: 'bg-orange-100 text-orange-600' },
  URGENT: { label: 'Urgent', color: 'bg-red-100 text-red-600' }
};

const TASK_TYPE_ICONS = {
  DOCUMENT_UPLOAD: Upload,
  QUESTIONNAIRE: FileText,
  VIDEO_CALL: Eye,
  FORM_SUBMISSION: FileText,
  REVIEW: Eye
};

export default function TaskCard({
  task,
  onSubmit,
  onView,
  onReview,
  showActions = true,
  expandable = true,
  variant = 'default' // default, compact
}) {
  const [expanded, setExpanded] = useState(false);

  const statusConfig = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.PENDING;
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.MEDIUM;
  const StatusIcon = statusConfig.icon;
  const TaskIcon = TASK_TYPE_ICONS[task.taskType] || FileText;

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'COMPLETED';

  const getProgressValue = () => {
    const statusOrder = ['PENDING', 'IN_PROGRESS', 'SUBMITTED', 'UNDER_REVIEW', 'COMPLETED'];
    const index = statusOrder.indexOf(task.status);
    if (task.status === 'REVISION_REQUIRED') return 60;
    return index >= 0 ? ((index + 1) / statusOrder.length) * 100 : 0;
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), 'MMM d, yyyy');
    } catch {
      return 'N/A';
    }
  };

  if (variant === 'compact') {
    return (
      <div
        className={`p-4 rounded-lg border ${statusConfig.bgColor} border-gray-200 hover:shadow-sm transition-shadow cursor-pointer`}
        onClick={() => onView?.(task)}
        data-testid={`task-card-${task.taskId}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <TaskIcon className="w-4 h-4 text-gray-600" />
            </div>
            <div>
              <p className="font-medium text-sm text-gray-900">{task.title}</p>
              <p className="text-xs text-gray-500">{task.taskType?.replace(/_/g, ' ')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            {isOverdue && <Badge className="bg-red-100 text-red-700">Overdue</Badge>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card
      className={`overflow-hidden ${statusConfig.bgColor} border-l-4 ${
        task.status === 'COMPLETED' ? 'border-l-green-500' :
        task.status === 'REVISION_REQUIRED' ? 'border-l-orange-500' :
        isOverdue ? 'border-l-red-500' : 'border-l-blue-500'
      }`}
      data-testid={`task-card-${task.taskId}`}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <TaskIcon className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{task.title}</h4>
              <p className="text-sm text-gray-500">{task.taskType?.replace(/_/g, ' ')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
            <Badge className={statusConfig.color}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{Math.round(getProgressValue())}%</span>
          </div>
          <Progress value={getProgressValue()} className="h-1.5" />
        </div>

        {/* Meta Info */}
        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
          {task.dueDate && (
            <div className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : ''}`}>
              <Calendar className="w-4 h-4" />
              <span>Due: {formatDate(task.dueDate)}</span>
              {isOverdue && <Badge className="bg-red-100 text-red-700 text-xs ml-1">Overdue</Badge>}
            </div>
          )}
          {task.assignedBy && (
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span>Assigned by: {task.assignedByName || 'Counselor'}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {task.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{task.description}</p>
        )}

        {/* Expandable Content */}
        {expandable && (
          <>
            {expanded && (
              <div className="space-y-4 mb-4 pt-4 border-t border-gray-200">
                {/* Instructions */}
                {task.instructions && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1">Instructions:</p>
                    <p className="text-sm text-gray-600 bg-white p-3 rounded-lg">{task.instructions}</p>
                  </div>
                )}

                {/* Submission */}
                {task.submission && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1">Your Submission:</p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-sm text-gray-600">{task.submission.text}</p>
                      {task.submission.files?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {task.submission.files.map((file, idx) => (
                            <a
                              key={idx}
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              <FileText className="w-3 h-3" />
                              {file.name}
                            </a>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        Submitted {formatDistanceToNow(new Date(task.submission.submittedAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                )}

                {/* Feedback */}
                {task.feedback && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      Feedback:
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-sm text-gray-600">{task.feedback.text}</p>
                      {task.feedback.rating && (
                        <div className="flex items-center gap-1 mt-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < task.feedback.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        {formatDistanceToNow(new Date(task.feedback.providedAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Show more
                </>
              )}
            </button>
          </>
        )}

        {/* Actions */}
        {showActions && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView?.(task)}
            >
              <Eye className="w-4 h-4 mr-1" />
              View Details
            </Button>

            {(task.status === 'PENDING' || task.status === 'IN_PROGRESS' || task.status === 'REVISION_REQUIRED') && onSubmit && (
              <Button
                size="sm"
                onClick={() => onSubmit?.(task)}
              >
                <Upload className="w-4 h-4 mr-1" />
                Submit
              </Button>
            )}

            {(task.status === 'SUBMITTED' || task.status === 'UNDER_REVIEW') && onReview && (
              <Button
                size="sm"
                onClick={() => onReview?.(task)}
              >
                <Eye className="w-4 h-4 mr-1" />
                Review
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
